import { Button, Dropdown, InputNumber } from "antd";
import { ColumnProps } from "antd/lib/table";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import CategoryContext from "../../../context/category/context";
import { ISort, IStaticFilter } from "../../../models/base/base-query";
import { ICategory } from "../../../models/category/response";
import eventManager, { EVENT_SUCCESS } from "../../../utils/events";
import { tableOnChange } from "../../../utils/helpers/table-sorts-filters";
import Space from "../../general/antd/space";
import Tag from "../../general/antd/tag";
import DeleteBtn from "../../general/table-components/actions/delete-btn";
import EditBtn from "../../general/table-components/actions/edit-btn";
import ViewBtn from "../../general/table-components/actions/view-btn";
import Table from "../../general/table-components/table";
import { DndContext, DragEndEvent, closestCenter } from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import EndPoints from "../../../services/end-points";
import { isEmpty } from "lodash";
import { BarsOutlined, MoreOutlined, ShopOutlined } from "@ant-design/icons";
import Image from "../../general/antd/image";
import Popconfirm from "../../general/antd/pop-confirm";
import AppContext from "../../../context/app/context";

interface RowContextProps {
  setActivatorNodeRef?: (element: HTMLElement | null) => void;
  listeners?: any;
}
const RowContext = React.createContext<RowContextProps>({});


const DragHandle = ({ isSortingEnabled }: { isSortingEnabled: boolean }) => {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);

  return (
    <Button
      type="text"
      size="small"
      icon={<BarsOutlined />}
      style={{
        cursor: isSortingEnabled ? "grab" : "not-allowed",
        opacity: isSortingEnabled ? 1 : 0.5,
      }}
      ref={setActivatorNodeRef}
      {...(isSortingEnabled ? listeners : {})}
      disabled={!isSortingEnabled}
    />
  );
};

const Row: React.FC<{ "data-row-key": string } & React.HTMLAttributes<HTMLTableRowElement>> = (props) => {
  const { attributes, listeners, setNodeRef, setActivatorNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  });

  const style: React.CSSProperties = {
    ...props.style,
    transform: CSS.Translate.toString(transform),
    transition,
    ...(isDragging ? { position: "relative", zIndex: 9999 } : {}),
  };

  const contextValue = useMemo(() => ({ setActivatorNodeRef, listeners }), [setActivatorNodeRef, listeners]);

  return (
    <RowContext.Provider value={contextValue}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes} />
    </RowContext.Provider>
  );
};

interface IProps {
  isSortingEnabled: boolean;
  setLoadingCat: (loadingCat: boolean) => void
}
const CategoryTable: React.FC<IProps> = ({ isSortingEnabled, setLoadingCat }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { list, actions, loading, query } = useContext(CategoryContext);
  const [dataSource, setDataSource] = useState(list?.data ?? []);
  const [loadingUpdate, setLoadingUpdate] = useState(true);
  const { screenSize } = useContext(AppContext);


  useEffect(() => {
    if (!isSortingEnabled && list?.data) {
      setDataSource(list.data);
      setLoadingUpdate(false);
      setLoadingCat(false);
    }
    if (isSortingEnabled) {
      const getCategories = async () => {
        try {
          const checkFilters = isEmpty(query?.filters) || !query?.filters || query?.filters === undefined;
          const existingFilters = query?.filters?.map((filter) => ({
            ...filter,
            value: filter.value !== undefined ? filter.value : null,
          })) ?? [];

          const requiredFilters = [
            { name: "grandId", value: null, operation: "eq" },
            { name: "parentId", value: null, operation: "eq" },
          ];

          const finalFilters = checkFilters
            ? requiredFilters
            : [
              ...existingFilters,
              ...requiredFilters.filter(
                (reqFilter) => !existingFilters.some((f) => f.name === reqFilter.name)
              ),
            ];

          const { data } = await EndPoints.category.getAllCategories({ filters: finalFilters });
          setDataSource(data.data);
          setLoadingCat(false)
        } catch (error) {
          console.error("Error fetching categories:", error);
        } finally {
          setLoadingUpdate(false);
        }
      };


      getCategories();

    }
  }, [list?.data, isSortingEnabled, query]);

  const onDragEnd = async ({ active, over }: DragEndEvent) => {
    if (!isSortingEnabled) return;
    if (!over) return;

    const activeId = Number(active.id);
    const overId = Number(over.id);

    const movedItem = dataSource.find((item) => item.id === activeId);
    const overItem = dataSource.find((item) => item.id === overId);

    if (movedItem && overItem) {
      const updatedDataSource = arrayMove(dataSource, dataSource.indexOf(movedItem), dataSource.indexOf(overItem));
      setDataSource(updatedDataSource);

      await handlePriorityChange(movedItem.id, overItem.priority);
    }
  };

  const handlePriorityChange = async (id: number, newPriority: number | null) => {
    if (newPriority === null) return;

    const updatedDataSource = dataSource.map((item) =>
      item.id === id ? { ...item, priority: newPriority } : item
    );

    const sortedDataSource = [...updatedDataSource].sort((a, b) => a.priority - b.priority);
    setDataSource(sortedDataSource);

    await actions.updateCategoriesArrangementList(id, { priority: newPriority });
    await actions.getData();
  };


  const columns: ColumnProps<ICategory>[] = [
    { key: "sort", align: "center", width: 80, render: () => <DragHandle isSortingEnabled={isSortingEnabled} /> },
    {
      title: t("id"),
      dataIndex: "id",
      key: "id",
      sorter: {
        multiple: 2,
      },
      width: 100,
      align: "center",
    },
    {
      title: t("name"),
      dataIndex: "name",
      align: "center",
      key: "name",
      sorter: {
        multiple: 2,
      },
    },
    {
      title: t("image"),
      dataIndex: "image",
      align: "center",
      key: "image",
      render: (_, record) => {
        return (
          <Image
            src={record.image.url}
            preview={true}
          />
        );
      },
    },
    {
      title: t("grand"),
      dataIndex: "grand",
      width: 200,
      align: "center",
      key: "parent",
      render: (_, record) => {
        return (
          <>
            {record.grand ? (
              <Tag title={record?.grand?.name} color="#d524d2" />
            ) : (
              "-"
            )}
          </>
        );
      },
    },
    {
      title: t("parent"),
      dataIndex: "parent",
      width: 200,
      align: "center",
      key: "parent",
      render: (_, record) => {
        return (
          <>
            {record.parent ? (
              <Tag title={record?.parent?.name} color="#2F4E8E" />
            ) : (
              "-"
            )}
          </>
        );
      },
    },
    {
      title: t("priority"),
      dataIndex: "priority",
      key: "priority",
      align: "center",
    },
    {
      title: t("order"),
      dataIndex: "order",
      align: "center",
      key: "order",
    },
    {
      title: t("percent"),
      dataIndex: "percent",
      align: "center",
      key: "percent",
    },
    {
      title: t("created_at"),
      dataIndex: "createdAt",
      align: "center",
      key: "createdAt",
      render: (createdAt) => {
        return moment(createdAt).format("YYYY-MM-DD");
      },
    },
    {
      title: t("actions"),
      dataIndex: "",
      width: screenSize != 'mobileOrTablet' ? 310 : 90,
      fixed: "right",
      align: "center",
      key: "x",
      render: (_: any, record) => (
        screenSize === 'mobileOrTablet' ? (
          <Dropdown
            trigger={["click"]}
            menu={{
              items: [
                {
                  key: 'companies',
                  label: (
                    <Button
                      type="link"
                      onClick={async () => {
                        navigate(`${record?.id}/companies`, { state: { categoryId: record?.id } })
                      }}
                    >
                      {t("companies")}
                    </Button>
                  ),
                },
                {
                  key: 'attributes',
                  label: (
                    <Button
                      type="link"
                      disabled={
                        record?.grand !== null && record?.parent === null ? false : true
                      }
                      onClick={async () => {
                        navigate(`${record?.id}/attributes`);
                      }}
                    >
                      {t("attributes")}
                    </Button>
                  ),
                },
                {
                  key: 'view',
                  label: (
                    <Button
                      type="link"
                      onClick={(e) => {
                        const url = `/categories/details/${record?.id}`;
                        if (e.ctrlKey || e.metaKey) {
                          window.open(url, "_blank");
                        } else {
                          navigate(url);
                        }
                      }}
                    >
                      {t("view")}
                    </Button>
                  ),
                },
                {
                  key: 'edit',
                  label: (
                    <Button
                      type="link"
                      onClick={(e) => {
                        const url = `/categories/update/${record?.id}`;
                        if (e.ctrlKey || e.metaKey) {
                          window.open(url, "_blank");
                        } else {
                          navigate(url);
                        }
                      }}
                    >
                      {t("edit")}
                    </Button>
                  ),
                },
                {
                  key: 'delete',
                  label: (
                    <Popconfirm
                      title={t("confirm")}
                      onConfirm={async () => {
                        await actions.deleteCategory(record?.id);
                        eventManager.emit(EVENT_SUCCESS);
                      }}
                    >
                      <Button type="link" danger>
                        {t("delete")}
                      </Button>
                    </Popconfirm>
                  ),
                },
              ],
            }}
          >
            <Button icon={<MoreOutlined />} />
          </Dropdown>
        ) : (
          <Space>
            <Button
              shape="circle"
              icon={<ShopOutlined />}
              onClick={async () => {
                navigate(`${record?.id}/companies`, { state: { categoryId: record?.id } })
              }}
            />
            <Button
              shape="circle"
              icon={<BarsOutlined />}
              disabled={
                record?.grand !== null && record?.parent === null ? false : true
              }
              onClick={async () => {
                navigate(`${record?.id}/attributes`);
              }}
            />
            <ViewBtn
              onClick={(e) => {
                const url = `/categories/details/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
            <EditBtn
              onClick={(e) => {
                const url = `/categories/update/${record?.id}`;
                if (e.ctrlKey || e.metaKey) {
                  window.open(url, "_blank");
                } else {
                  navigate(url);
                }
              }}
            />
            <DeleteBtn
              onConfirm={async () => {
                await actions.deleteCategory(record?.id);
                eventManager.emit(EVENT_SUCCESS);
              }}
              loading={loading.includes("delete")}
            />
          </Space>
        )
      ),
    }
  ];

  let tableFiltersProps = {};
  const [sorts, setSorts] = useState<ISort[]>([]);
  const [, setStaticFilters] = useState<IStaticFilter[]>(query?.filters ?? []);

  useEffect(() => {
    sorts.length > 0 &&
      actions.setQuery({
        ...query,
        orders: sorts,
      });
  }, [sorts]);

  return (
    <DndContext collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={dataSource.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <Table<any>
          rowKey="id"
          showPagination={true}
          columns={columns.filter(column => column.key !== "priority" || isSortingEnabled)}
          dataSource={dataSource}
          loading={loading.includes("list") || list == undefined}
          total={list?.total}
          size="small"
          scroll={{
            x: 1400,
          }}
          pageSize={query.perPage}
          page={query.page}
          onPaginationChange={(page, pageSize) => {
            actions.setQuery({
              ...query,
              page,
              perPage: pageSize,
            });
          }}
          onChange={(_, tableFilters, tabelSorters) => {
            tableOnChange(
              tableFilters,
              tabelSorters,
              tableFiltersProps,
              setSorts,
              setStaticFilters
            );
          }}
          components={{
            body: {
              row: Row,
            },
          }}
        />
      </SortableContext>
    </DndContext>
  );
};

export default CategoryTable;